import type { ReactNode } from "react";
import { Link } from "@tanstack/react-router";
import { ArrowRight, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Overline } from "@/components/site/Overline";
import { business } from "@/content/site";
import { deliveryVariants, imageSrcSet } from "@/lib/image-delivery";
import { focalToObjectPosition, type FocalPoint } from "@/lib/media-taxonomy";

type CtaSplitProps = {
  overline?: string;
  title: string;
  text: ReactNode;
  imageSrc: string;
  imageAlt: string;
  imageFocal?: FocalPoint | null | undefined;
  ctaLabel?: string;
  ctaTo?: string;
};

export function CtaSplit({
  overline = "Klaar voor uw zwembadproject?",
  title,
  text,
  imageSrc,
  imageAlt,
  imageFocal,
  ctaLabel = "Vraag een offerte",
  ctaTo = "/offerte",
}: CtaSplitProps) {
  return (
    <section className="grid bg-primary text-primary-foreground lg:grid-cols-2">
      <img
        src={imageSrc}
        srcSet={imageSrcSet(deliveryVariants(imageSrc))}
        sizes="(min-width: 1024px) 50vw, 100vw"
        alt={imageAlt}
        width={1024}
        height={768}
        loading="lazy"
        decoding="async"
        {...(imageFocal ? { style: { objectPosition: focalToObjectPosition(imageFocal) } } : {})}
        className="h-full min-h-72 w-full object-cover lg:min-h-96"
      />
      <div className="flex items-center px-5 py-16 lg:px-16 lg:py-24">
        <div>
          <Overline onDark>{overline}</Overline>
          <h2 className="mt-4 display-section">
            {title}
          </h2>
          <p className="mt-5 max-w-[70ch] text-[17px] leading-7 text-primary-foreground/80">{text}</p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Button asChild size="lg" className="bg-secondary text-secondary-foreground hover:bg-secondary/90">
              <Link to={ctaTo}>
                {ctaLabel} <ArrowRight className="ml-1 h-4 w-4" />
              </Link>
            </Button>
            <Button
              asChild
              size="lg"
              variant="outline"
              className="border-primary-foreground/60 bg-transparent text-primary-foreground hover:bg-primary-foreground/10 hover:text-primary-foreground"
            >
              <a href={`tel:${business.phoneE164}`}>
                <Phone className="mr-2 h-4 w-4" /> {business.phone}
              </a>
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
}
